"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePopOutOfOverflowHiddenParent = void 0;
var react_1 = require("react");
var useScroll_1 = require("./customHooks/useScroll");
//HOW TO USE
/**
 * this utility is useful when action menus or dropdowns are
 * used inside parents that have overflow hidden.
 *
 * When the dropdown / menu opens, we need it to show,
 * and sometimes its not possible to reposition the dropdown / menu
 * outside of the overflow hidden container.
 *
 * In such cases, we can use this hook.
 * The way to use is this:
 *
 * 1// initiate -> (see what it returns below)
 *
 * 2// attach the ref & pass fixPos as a prop to whatever
 * wrapper is wrapping the menu or dropdown.
 *
 * 3// whereever you are tracking the dropdown/menu opening or closing,
 * there you can write a function to
 * trigger applyFixedPos() when open
 * and removeFixedPos() when closed
 * for example in ActionMenu we can pass a prop called 'setShowActions'
 * which listens to open-close changes
 *
 * And thats it. it should work.
 *
 * Current Usage :
 * 1. In ImageEditor component > to make the ActionMenu stick out of parent
 * 2. In TableRowElem component > to make the ActionMenu stick out of parent
 *
 * Known Issues :
 * 1. If you scroll while the menu is open, it remains fixed to that part of the
 * screen since we are using fixed positioning.
 * 2. When you close the menu, fixed position is removed, so momentarily you see the
 * menu disappearing behind the overflow hidden parent. Minor, but an issue none the less.
 *
 *
 *
 */
/**
 * Creates a custom hook that enables an element to pop out of its overflow hidden parent.
 *
 * @param {boolean} popOutOfOverflowHiddenParent - Determines if the element should pop out of its overflow hidden parent.
 * @return {object} An object containing the following properties:
 *   - {RefObject<HTMLDivElement>} trackerRef: A ref to the tracker div element.
 *   - {Function} applyFixedPos: A function that applies the fixed position to the element.
 *   - {Function} removeFixedPos: A function that removes the fixed position from the element.
 *   - {undefined | ClientRect} fixPos: The fixed position of the element.
 */
var usePopOutOfOverflowHiddenParent = function (popOutOfOverflowHiddenParent) {
    var enabled = popOutOfOverflowHiddenParent;
    var trackerRef = (0, react_1.useRef)(null);
    var _a = (0, react_1.useState)(), fixPos = _a[0], setFixPos = _a[1];
    var getBoundingClientRect = function () { var _a; return (_a = trackerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect(); };
    var applyFixedPos = function () { return enabled && setFixPos(getBoundingClientRect()); };
    var updateFixedPos = function () {
        if (popOutOfOverflowHiddenParent) {
            setFixPos(function (prev) { return prev && getBoundingClientRect(); });
        }
    };
    var removeFixedPos = function () { return enabled && setFixPos(undefined); };
    (0, useScroll_1.useScroll)(updateFixedPos, { noThrottle: true, detectScrollInAllDivs: true });
    return { trackerRef: trackerRef, applyFixedPos: applyFixedPos, removeFixedPos: removeFixedPos, fixPos: fixPos };
};
exports.usePopOutOfOverflowHiddenParent = usePopOutOfOverflowHiddenParent;
